import { canOpenView } from './matrix';
import type { ViewKey } from './role';

/**
 * Where a person opens a case, by the screens they hold. Section 13 and 14 of
 * the review of 2 October 2026.
 *
 * Every link to a case used to name one route, and the shell's guard silently
 * sent anybody without that route's screen back home: Accounts tapping a row on
 * Payments, Reception tapping a tray in an order, a technician tapping a bell.
 * The link now asks this for the first door the person has, in the order the
 * link prefers, and offers nothing when there is none.
 *
 * - `kit`: the job view, which the queue, the line and Final Check open.
 * - `order`: the orders drawer.
 * - `record`: the full record, on Orders or, for the archive's holders, on the
 *   archive's own record screen, which serves a live case as well.
 */
export type CaseDoor = 'kit' | 'order' | 'record';

export interface CaseTarget {
  jobOrderNo: string;
  kitId?: string | null;
}

export function casePath(
  views: readonly ViewKey[],
  target: CaseTarget,
  prefer: CaseDoor,
): string | null {
  const jo = target.jobOrderNo;
  const kit =
    target.kitId !== undefined && target.kitId !== null && canOpenView(views, 'job')
      ? `/queue/${target.kitId}`
      : null;
  const order = views.includes('orders') ? `/orders/${jo}` : null;
  const record = views.includes('orders')
    ? `/orders/${jo}/record`
    : canOpenView(views, 'record')
      ? `/archive/${jo}`
      : null;

  const doors: Record<CaseDoor, (string | null)[]> = {
    kit: [kit, order, record],
    order: [order, kit, record],
    record: [record, kit],
  };
  return doors[prefer].find((path) => path !== null) ?? null;
}

/**
 * Whether a job order number alone leads this person to the archive's record:
 * they hold the archive and not Orders. The request of 5 October 2026 (third),
 * section 10.
 *
 * Find case and the bell look a job order up before they open it. The lookup
 * used to list the trays of the floor, which is a call of the bench screens and
 * is refused to an accountant who holds only Payments, Items and Archive; for
 * somebody whose door is the record, the record is what is asked for instead.
 * It is asked of the screens held and never of the role, so an accountant
 * given Orders is led to the order like anybody else who holds it (the request
 * of 6 October 2026 (fourth)). It also finds a case the floor's list never
 * returns, one that has been shipped or archived.
 */
export function findsCaseInArchive(views: readonly ViewKey[]): boolean {
  return casePath(views, { jobOrderNo: '' }, 'order')?.startsWith('/archive/') === true;
}
